import { Config } from '@/config';
import { ApiService } from '@/services/api.service';

/**
 * Per-tenant terminology backend (tenant resolved server-side from the JWT).
 *
 * - `getOverlay()` is the bootstrap read: the delta overlay applied to the i18n lexicon at login
 *   (shape `{ terms: { '<locale>': { '<entity>': { gender, startsWithVowelSound, forms } } } }`).
 *   Empty `{ terms: {} }` means "no customization" → defaults are used.
 * - The `admin*` methods back the manager-only editor (ROLE_MANAGER+; 403 otherwise). Admins send
 *   only the 4 base inputs (singular/plural/gender/startsWithVowelSound) + optional per-form
 *   overrides; the backend derives and returns the 15 forms.
 */
export const BASE_URL = `${Config.api.tenantSettings.url}api/v1/tenants/terminology`;

class TenantTerminologyService {
  /** Bootstrap overlay (deltas only). Any authenticated user. */
  getOverlay() {
    return ApiService.get(BASE_URL).then((response) => response.data);
  }

  /** Editor snapshot: registry, locales, form keys, and current stored customizations. */
  getAdminSnapshot() {
    return ApiService.get(`${BASE_URL}/admin`).then((response) => response.data);
  }

  /** Live preview of derived forms for the given base inputs. Does not save. */
  preview(input) {
    return ApiService.post(`${BASE_URL}/admin/preview`, input).then((response) => response.data);
  }

  /** Save (upsert) a term for one (entity, locale). Backend derives the 15 forms. */
  saveTerm(entityId, locale, input) {
    return ApiService.put(`${BASE_URL}/admin/${entityId}/${locale}`, input).then((response) => response.data);
  }

  /** Reset a term for one (entity, locale) back to the built-in default. */
  resetTerm(entityId, locale) {
    return ApiService.delete(`${BASE_URL}/admin/${entityId}/${locale}`).then((response) => response.data);
  }
}

export const tenantTerminologyService = new TenantTerminologyService();
